import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';
import * as g from '../../castle-kit-grounds';

/**
 * ROYAL GARDENS (Королевский сад) — the open-air PLEASURE GARDEN of Castle Hearthhold: the one room
 * where GREEN belongs (Rule 10). Recomposed for legibility around TWO clear beats with open lawn between:
 *
 *   • WEST — a glassy LILY POND crossed by a little arched bridge, with a pondside bench to linger on,
 *     screened by a short clipped hedge so it reads as a quiet water nook (the old scattered "maze" of
 *     disconnected hedge chunks didn't read from top-down — replaced by one tidy hedge run + a votive
 *     shrine tucked beside the water).
 *   • EAST-CENTRE — the HERO domed GAZEBO ducks gather inside (lantern-warm, three ringing chairs),
 *     a SUNDIAL at its foot, framed by two blossom trees and a pair of flower borders.
 *
 * Topiary thinned to two ornamental punctuation balls; two lampposts pool warm light on pond + gazebo.
 * Empty lawn between the two beats is intentional breathing room (Rule: empty space is design).
 *
 * Geometry: zone (2800,1180) 760×520 → centre (3180,1440). Doors W@y1440, N@x2990, N@x3370, S@x3180 are
 * kept clear (the central cross + each door mouth stay walkable; set-dressing hugs the interior + edges).
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;

  // ── WEST water nook: the LILY POND + arched bridge + pondside bench (seat anchor garden-pond). ──
  place(ctx, g.pond(art, 150, 100), 3000, 1380);
  place(ctx, g.archBridge(art, 110), 3000, 1300);
  place(ctx, g.gardenBench(art), 3000, 1300); // pondside seat anchor (sit-match)
  // One tidy hedge run screens the pond's north edge so it reads as a secluded nook; a votive shrine
  // glows just west of the water (warm accent + the garden's quiet focal moment on this side).
  place(ctx, g.hedge(art, 150, 34), 2960, 1280);
  place(ctx, kit.votiveStand(art), 2880, 1380);
  // A single blossom tree anchors the SW corner of the lawn.
  place(ctx, g.tree(art, 'blossom'), 2900, 1600);

  // ── HERO: the domed garden GAZEBO (east-centre) — the gather magnet, lantern-warm + central. ──
  place(ctx, g.gazebo(art, 74), 3300, 1440);
  // Gazebo seats (sit-match): three wood chairs ringing the inside of the dome.
  place(ctx, kit.woodChair(art), 3300, 1300);
  place(ctx, kit.woodChair(art), 3300, 1580);
  place(ctx, kit.woodChair(art), 3420, 1440);
  // A SUNDIAL at the gazebo's foot (centre) — the garden's quiet timepiece; engine-prop halo kept clear.
  place(ctx, g.sundial(art), 3180, 1440);

  // ── Blossoming TREES + flower borders framing the eastern lawn (green lives outdoors). ──
  place(ctx, g.tree(art, 'blossom'), 3460, 1280);
  place(ctx, g.tree(art, 'blossom'), 3460, 1600);
  place(ctx, g.flowerBed(art), 3200, 1240);
  place(ctx, g.flowerBed(art), 3380, 1640);

  // ── Two clipped TOPIARY balls as ornamental punctuation — off the walking paths. ──
  place(ctx, g.topiary(art, 'ball'), 3120, 1620);
  place(ctx, g.topiary(art, 'cone'), 3260, 1240);

  // ── POOL LIGHT: lampposts warm the pond nook (W) + the gazebo's blossom walk (SE). ──
  place(ctx, g.lamppost(art), 2880, 1280);
  place(ctx, g.lamppost(art), 3500, 1560);
};

export default decorate;
